import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusChip } from "@/components/StatusChip";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus } from "lucide-react";

export default function Projects() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [summaries, setSummaries] = useState({});
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const canCreate = user?.role === "admin" || user?.role === "budget_controller";

  const load = async () => {
    const { data } = await api.get("/projects");
    setProjects(data || []);
    const smap = {};
    for (const p of data || []) {
      try { const { data: s } = await api.get(`/projects/${p.id}/summary`); smap[p.id] = s; } catch {}
    }
    setSummaries(smap);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (canCreate) api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {});
  }, [canCreate]);

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#0D3B66] tracking-tight">Projects</h1>
          <p className="mt-1.5 text-slate-500">Active engineering projects across your portfolio.</p>
        </div>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-project-btn" className="rounded-full bg-[#F97316] hover:bg-[#EA6A0E]"><Plus className="h-4 w-4 mr-1.5" /> New Project</Button>
            </DialogTrigger>
            <NewProjectDialog users={users} onDone={() => { setOpen(false); load(); }} />
          </Dialog>
        )}
      </div>
      {projects.length === 0 ? (
        <div data-testid="empty-projects" className="rounded-lg border border-dashed border-slate-300 p-10 text-center text-slate-500">No projects yet.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((p) => {
            const s = summaries[p.id];
            const pct = s?.consumption_pct || 0;
            return (
              <Link key={p.id} to={`/projects/${p.id}`} data-testid={`project-card-${p.id}`}>
                <Card className="p-5 border-slate-200 hover:border-[#F97316] hover:shadow-sm transition-all">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-base font-semibold text-[#0D3B66]">{p.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{p.location || "—"}</div>
                    </div>
                    <StatusChip status={p.status} />
                  </div>
                  <div className="mt-5 grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <div className="text-slate-500 uppercase tracking-wider">Budget</div>
                      <div className="text-slate-800 font-semibold mt-0.5">{fmtINR(s?.total_budget)}</div>
                    </div>
                    <div>
                      <div className="text-slate-500 uppercase tracking-wider">Version / Status</div>
                      <div className="text-slate-800 font-semibold mt-0.5">v{s?.budget_version || 0} · {s?.budget_status || "-"}</div>
                    </div>
                  </div>
                  <div className="mt-3 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#F97316] to-[#FBBF24]" style={{ width: `${Math.min(pct, 100)}%` }} />
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function NewProjectDialog({ users, onDone }) {
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [status, setStatus] = useState("Active");
  const [bc, setBc] = useState("");
  const [eh, setEh] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const bcs = users.filter((u) => u.role === "budget_controller");
  const ehs = users.filter((u) => u.role === "engineering_head");

  const save = async () => {
    setErr(""); setSaving(true);
    try {
      await api.post("/projects", {
        name, location, status,
        budget_controller_id: bc || null,
        engineering_head_id: eh || null,
        site_engineer_ids: users.filter((u) => u.role === "site_engineer").map((u) => u.id),
      });
      onDone();
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <DialogContent data-testid="new-project-dialog">
      <DialogHeader><DialogTitle>New Project</DialogTitle></DialogHeader>
      <div className="space-y-4 pt-2">
        <div className="space-y-1.5">
          <Label>Name</Label>
          <Input data-testid="project-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Riverwood SBA" />
        </div>
        <div className="space-y-1.5">
          <Label>Location</Label>
          <Input data-testid="project-location-input" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="City / Site" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="project-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="On Hold">On Hold</SelectItem>
                <SelectItem value="Closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3">
          <div className="space-y-1.5">
            <Label>Budget Controller</Label>
            <Select value={bc} onValueChange={setBc}>
              <SelectTrigger data-testid="project-bc-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {bcs.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Engineering Head</Label>
            <Select value={eh} onValueChange={setEh}>
              <SelectTrigger data-testid="project-eh-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {ehs.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        {err && <div className="text-sm text-rose-700">{String(err)}</div>}
      </div>
      <DialogFooter>
        <Button data-testid="project-save-btn" onClick={save} disabled={saving || !name} className="bg-[#0D3B66] hover:bg-[#114B82]">
          {saving ? "Saving…" : "Create"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
